import { useEffect, useRef, useState } from "react";

import { getItemFromLocalStorage, setItemInLocalStorage } from "./localStorage";
import { getItemFromSessionStorage, setItemInSessionStorage } from "./sessionStorage";

// Per-tab state for values a later page load in the SAME tab must read back
// (e.g. booking ids across a hosted-payment redirect). The tab's sessionStorage
// copy always wins, so another tab's write can never replace it — which the
// localStorage-only useSessionStorage allowed. Every change is mirrored to
// localStorage only so a brand-new browsing context with no tab copy yet still
// starts from the last written value, as it did before.
export function useTabScopedStorage<T>(key: string, initialValue: T | (() => T)) {
  const [value, setValue] = useState<T>(() => {
    try {
      if (sessionStorage.getItem(key) !== null) {
        // This tab's own copy, including an explicit null (a deliberate clear).
        const own = getItemFromSessionStorage(key);
        if (own !== "undefined") return own;
      } else {
        const shared = getItemFromLocalStorage(key);
        if (shared != null && shared !== "undefined") return shared;
      }
    } catch (e) {
      console.warn("Error reading tab-scoped storage:", e);
    }
    return typeof initialValue === "function" ? (initialValue as () => T)() : initialValue;
  });

  // Write on changes only: re-writing the mounted value would let an idle tab
  // that reloads overwrite another tab's newer localStorage copy.
  const isMount = useRef(true);
  useEffect(() => {
    if (isMount.current) {
      isMount.current = false;
      return;
    }
    setItemInLocalStorage(key, value);
    // The write helpers swallow errors (e.g. a full sessionStorage). Drop the old
    // tab copy first so a failed write falls back to localStorage on the next
    // read instead of leaving a stale copy that wins.
    try {
      sessionStorage.removeItem(key);
    } catch {}
    setItemInSessionStorage(key, value);
  }, [key, value]);

  return [value, setValue] as [typeof value, typeof setValue];
}
